// frontend/src/hooks/useIsMobile.js
import { useState, useEffect } from 'react';

// Mobile breakpoint — keep in sync with the @media query in styles/responsive.css
export const MOBILE_BREAKPOINT = 768;

/**
 * Returns true when the viewport width is at or below the mobile breakpoint.
 * Re-renders the consuming component whenever the breakpoint is crossed.
 *
 * @param {number} [breakpoint=MOBILE_BREAKPOINT] max-width (px) considered "mobile"
 */
export default function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
    const query = `(max-width: ${breakpoint}px)`;
    const getMatch = () =>
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(query).matches
            : false;

    const [isMobile, setIsMobile] = useState(getMatch);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mql = window.matchMedia(query);
        const handler = (e) => setIsMobile(e.matches);
        // Sync immediately in case the query changed between render and effect
        setIsMobile(mql.matches);
        // addEventListener is the modern API; addListener is the Safari < 14 fallback
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else mql.removeListener(handler);
        };
    }, [query]);

    return isMobile;
}
